import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  Activity,
  HeartPulse,
  Sparkles,
  Stethoscope,
  Users,
  Shield,
  LogOut,
  CircuitBoard,
  Smartphone,
  Heart,
  Puzzle,
  Mic,
  Brain,
  Radar,
  FileText,
  BookOpen,
  UserCircle2,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { LanguageSwitcher } from "@/components/layout/LanguageSwitcher";
import { MeAIPresence } from "@/components/layout/MeAIPresence";
import { APP_NAME } from "@/lib/brand";

const ROLE_LABEL = {
  member: { tr: "Üye", en: "Member" },
  clinician: { tr: "Klinisyen", en: "Clinician" },
  administrator: { tr: "Yönetici", en: "Administrator" },
};

function NavItem({ to, icon: Icon, label, testid, end }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testid}
      className={({ isActive }) =>
        `group flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-all duration-300 ${
          isActive
            ? "bg-[rgba(124,196,255,0.10)] text-[#e6efff] shadow-[inset_0_0_0_1px_rgba(124,196,255,0.25)]"
            : "text-[#9fb1d4] hover:text-[#e6efff] hover:bg-[rgba(124,196,255,0.05)]"
        }`
      }
    >
      <Icon className="h-4 w-4" strokeWidth={1.5} />
      <span className="truncate">{label}</span>
    </NavLink>
  );
}

export function AppShell({ children }) {
  const { user, logout } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  if (!user) return null;

  const memberNav = (
    <>
      <NavItem to="/dashboard" end icon={Activity} label={t.nav.overview} testid="nav-overview" />
      <NavItem to="/medical" icon={HeartPulse} label={t.nav.medical} testid="nav-medical" />
      <NavItem to="/lifestyle" icon={Sparkles} label={t.nav.lifestyle} testid="nav-lifestyle" />
      <NavItem to="/life" icon={Heart} label={t.nav.life} testid="nav-life" />
      <NavItem to="/assistant" icon={CircuitBoard} label={t.nav.assistant} testid="nav-assistant" />
      <NavItem to="/production" icon={FileText} label={t.nav.production} testid="nav-production" />
      <NavItem to="/knowledge" icon={BookOpen} label={t.nav.knowledge} testid="nav-knowledge" />
      <NavItem to="/cognitive" icon={Brain} label={t.nav.cognitive} testid="nav-cognitive" />
      <NavItem to="/intent" icon={Radar} label={t.nav.intent} testid="nav-intent" />
      <NavItem to="/persons" icon={UserCircle2} label={t.nav.persons} testid="nav-persons" />
      <NavItem to="/devices" icon={Smartphone} label={t.nav.devices} testid="nav-devices" />
      <NavItem to="/conscious" icon={Mic} label={t.nav.conscious} testid="nav-conscious" />
      <NavItem to="/plugins" icon={Puzzle} label={t.nav.plugins} testid="nav-plugins" />
    </>
  );
  const clinicianNav = (
    <>
      <NavItem to="/clinician" end icon={Stethoscope} label={t.nav.patients} testid="nav-patients" />
      <NavItem to="/assistant" icon={CircuitBoard} label={t.nav.assistant} testid="nav-assistant" />
      <NavItem to="/production" icon={FileText} label={t.nav.production} testid="nav-production" />
      <NavItem to="/knowledge" icon={BookOpen} label={t.nav.knowledge} testid="nav-knowledge" />
      <NavItem to="/cognitive" icon={Brain} label={t.nav.cognitive} testid="nav-cognitive" />
      <NavItem to="/intent" icon={Radar} label={t.nav.intent} testid="nav-intent" />
      <NavItem to="/persons" icon={UserCircle2} label={t.nav.persons} testid="nav-persons" />
      <NavItem to="/devices" icon={Smartphone} label={t.nav.devices} testid="nav-devices" />
      <NavItem to="/conscious" icon={Mic} label={t.nav.conscious} testid="nav-conscious" />
      <NavItem to="/plugins" icon={Puzzle} label={t.nav.plugins} testid="nav-plugins" />
    </>
  );
  const adminNav = (
    <>
      <NavItem to="/admin" end icon={Users} label={t.nav.users} testid="nav-users" />
      <NavItem to="/admin/brain" icon={Shield} label={t.nav.brainStatus} testid="nav-brain-status" />
      <NavItem to="/assistant" icon={CircuitBoard} label={t.nav.assistant} testid="nav-assistant" />
      <NavItem to="/production" icon={FileText} label={t.nav.production} testid="nav-production" />
      <NavItem to="/knowledge" icon={BookOpen} label={t.nav.knowledge} testid="nav-knowledge" />
      <NavItem to="/cognitive" icon={Brain} label={t.nav.cognitive} testid="nav-cognitive" />
      <NavItem to="/intent" icon={Radar} label={t.nav.intent} testid="nav-intent" />
      <NavItem to="/persons" icon={UserCircle2} label={t.nav.persons} testid="nav-persons" />
      <NavItem to="/devices" icon={Smartphone} label={t.nav.devices} testid="nav-devices" />
      <NavItem to="/conscious" icon={Mic} label={t.nav.conscious} testid="nav-conscious" />
      <NavItem to="/plugins" icon={Puzzle} label={t.nav.plugins} testid="nav-plugins" />
    </>
  );

  return (
    <div className="meds-shell flex min-h-screen" data-testid="app-shell">
      {/* Sidebar — glass navy panel */}
      <aside
        className="hidden w-64 shrink-0 md:flex md:flex-col"
        style={{
          background: "linear-gradient(180deg, rgba(11,21,48,0.85) 0%, rgba(5,11,24,0.78) 100%)",
          borderRight: "1px solid rgba(124, 196, 255, 0.10)",
          backdropFilter: "blur(24px) saturate(140%)",
        }}
      >
        <div className="px-6 py-7">
          <div className="flex items-center gap-3">
            <div
              className="relative flex h-10 w-10 items-center justify-center rounded-xl"
              style={{
                background: "linear-gradient(135deg, rgba(124,196,255,0.18) 0%, rgba(37,99,235,0.32) 100%)",
                border: "1px solid rgba(124,196,255,0.28)",
                boxShadow: "0 0 0 1px rgba(255,255,255,0.04) inset, 0 8px 24px -10px rgba(60,124,220,0.5)",
              }}
            >
              <span
                className="text-sm font-semibold tracking-tight"
                style={{ color: "#e6efff" }}
              >
                Mi
              </span>
              <span
                className="absolute inset-0 rounded-xl"
                style={{
                  background: "radial-gradient(circle at 30% 25%, rgba(255,255,255,0.5) 0%, transparent 60%)",
                  opacity: 0.35,
                }}
              />
            </div>
            <div>
              <div className="text-base font-semibold tracking-tight" style={{ color: "#e6efff" }}>
                {APP_NAME}
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "#6b7da0" }}>
                {t.brand.tagline}
              </div>
            </div>
          </div>
        </div>
        <nav className="flex flex-1 flex-col gap-1 px-3 pb-6">
          {user.role === "member" && memberNav}
          {user.role === "clinician" && clinicianNav}
          {user.role === "administrator" && adminNav}
        </nav>
        <div
          className="p-4"
          style={{ borderTop: "1px solid rgba(124, 196, 255, 0.10)" }}
        >
          <div className="mb-3 flex items-center gap-3">
            <div
              className="flex h-9 w-9 items-center justify-center rounded-full text-sm font-medium"
              style={{
                background: "rgba(124, 196, 255, 0.10)",
                color: "#e6efff",
                border: "1px solid rgba(124, 196, 255, 0.18)",
              }}
            >
              {(user.name || user.email).slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium" data-testid="sidebar-user-name" style={{ color: "#e6efff" }}>
                {user.name}
              </div>
              <div className="truncate text-[11px]" style={{ color: "#6b7da0" }}>
                {(ROLE_LABEL[user.role] && ROLE_LABEL[user.role][language]) || user.role}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={async () => {
              await logout();
              navigate("/login");
            }}
            data-testid="logout-button"
            className="mios-btn mios-btn-ghost w-full"
          >
            <LogOut className="h-4 w-4" /> {t.nav.logout}
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex min-h-screen flex-1 flex-col">
        <header
          className="sticky top-0 z-10 flex items-center justify-between gap-3 px-6 py-3"
          style={{
            background: "linear-gradient(180deg, rgba(5,11,24,0.82) 0%, rgba(5,11,24,0.55) 100%)",
            borderBottom: "1px solid rgba(124, 196, 255, 0.08)",
            backdropFilter: "blur(22px)",
          }}
        >
          <div className="md:hidden">
            <div
              className="flex h-9 w-9 items-center justify-center rounded-lg text-sm font-semibold"
              style={{
                background: "linear-gradient(135deg, rgba(124,196,255,0.18) 0%, rgba(37,99,235,0.32) 100%)",
                color: "#e6efff",
              }}
            >
              Mi
            </div>
          </div>
          <div
            className="hidden md:block text-[10px] uppercase tracking-[0.22em]"
            style={{ color: "#6b7da0" }}
          >
            {t.brand.name} · v1.2 · MEDS
          </div>
          <div className="flex items-center gap-3">
            <MeAIPresence />
            <LanguageSwitcher />
          </div>
        </header>
        <div className="flex-1 px-6 py-8 lg:px-10 lg:py-10">{children}</div>
      </main>
    </div>
  );
}
